import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import api from "../../api";
import Logo from "../../images/logo-2.png";
import add from "../../images/add.png";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import { useAlert } from "react-alert";

// ── initial state helpers (same as AddPost) ───────────────────────
const INITIAL_SPOT = {
  exchange: "",
  coin: "",
  entryPrice: "",
  takeProfit: "",
  stopLoss: "",
  filledAmountUSDT: "",
  filledQuantityCoin: "",
  status: "Open",
  closedPrice: "",
};

const INITIAL_FUTURES = {
  exchange: "",
  coin: "",
  positionType: "",
  leverage: "",
  marginMode: "",
  entryPrice: "",
  stopLoss: "",
  takeProfit: "",
  marginUsedUSDT: "",
  positionSizeUSDT: "",
  liquidationPrice: "",
  status: "Open",
  closedPrice: "",
};

var Editor = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};

export function EditPost({ getData, setShow, editData }) {
  const adminStore = useSelector((state) => state.adminReducer);
  const alert = useAlert();

  const [loader, setLoader] = useState(false);
  const [selectedImg, setSelectedImg] = useState([]);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [inputData, setInputData] = useState({ video: "" });

  // ── trade state ───────────────────────────────────────────────────
  const [tradeType, setTradeType] = useState(null);
  const [spotTrade, setSpotTrade] = useState(INITIAL_SPOT);
  const [futuresTrade, setFuturesTrade] = useState(INITIAL_FUTURES);

  // ── populate all fields from editData ────────────────────────────
  useEffect(() => {
    setInputData({
      description: editData?.description || "",
      video: editData?.video || "",
      pdfFile: editData?.pdfFile || "",
      imageTitle: editData?.imageTitle || "",
      imageFormat: editData?.imageFormat || "",
    });

    // pre-fill trade type and trade data
    const type = editData?.tradeType || null;
    setTradeType(type);

    if (type === "spot" && editData?.spotTrade) {
      setSpotTrade({ ...INITIAL_SPOT, ...editData.spotTrade });
    }
    if (type === "futures" && editData?.futuresTrade) {
      setFuturesTrade({ ...INITIAL_FUTURES, ...editData.futuresTrade });
    }
  }, [editData]);

  // ── trade handlers ────────────────────────────────────────────────
  const handleSpotChange = (e) =>
    setSpotTrade({ ...spotTrade, [e.target.name]: e.target.value });
  const handleFuturesChange = (e) =>
    setFuturesTrade({ ...futuresTrade, [e.target.name]: e.target.value });

  const handleTradeTypeChange = (type) => {
    setTradeType(type === tradeType ? null : type);
  };

  const validateTrade = () => {
    if (tradeType === "spot") {
      const required = [
        "coin",
        "entryPrice",
        "takeProfit",
        "filledAmountUSDT",
        "filledQuantityCoin",
        "status", // ← add
      ];
      const missing = required.filter((f) => !spotTrade[f]);
      if (missing.length) {
        alert.info(`Missing required spot fields: ${missing.join(", ")}`);
        return false;
      }
      if (!spotTrade.coin.includes("/")) {
        alert.info("Coin must be in pair format e.g. BTC/USDT");
        return false;
      }
    }
    if (tradeType === "futures") {
      const required = [
        "coin",
        "positionType",
        "entryPrice",
        "stopLoss",
        "takeProfit",
        "status",
        "marginUsedUSDT",
      ];
      const missing = required.filter((f) => !futuresTrade[f]);
      if (missing.length) {
        alert.info(`Missing required futures fields: ${missing.join(", ")}`);
        return false;
      }
      if (!futuresTrade.coin.includes("/")) {
        alert.info("Coin must be in pair format e.g. BTC/USDT");
        return false;
      }
      if (Number(futuresTrade.marginUsedUSDT) <= 0) {
        alert.info("Balance Used (USDT) must be greater than 0");
        return false;
      }
    }
    return true;
  };

  // ── submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      if (!inputData.description && !tradeType) {
        alert.info("Please add a description or select a trade type");
        return;
      }
      if (!validateTrade()) return;

      setLoader(true);
      const formData = {
        _id: editData?._id,
        description: inputData?.description,
        video: inputData?.video || "",
        pdfFile: inputData?.pdfFile || "",
        imageTitle: inputData?.imageTitle || "",
        imageFormat: inputData?.imageFormat || "",
        // trade fields
        tradeType: tradeType || null,
        spotTrade: tradeType === "spot" ? spotTrade : null,
        futuresTrade: tradeType === "futures" ? futuresTrade : null,
      };

      const signalResponse = await api.postEdit(formData);

      if (signalResponse?.success) {
        alert.success("Signal edited successfully");
        setLoader(false);
        setShow(false);
        getData();
      } else {
        setLoader(false);
        alert.error("Something went wrong");
      }
    } catch (error) {
      setLoader(false);
      console.log(error);
      alert.error("Something went wrong");
    }
  };

  // ── file handlers — UNCHANGED ─────────────────────────────────────
  const onFileChange = async (e) => {
    try {
      setLoader(true);
      if (!e.target.files) return;
      const formData = new FormData();
      for (let file of e.target.files) formData.append("image", file);
      const uploadRes = await api.uploadFile(formData);
      let sImage = [];
      var imgName = uploadRes[0].split(",");
      var imgFormat = uploadRes[1].split(",");
      for (let i = 0; i < imgName.length; i++) {
        sImage.push(
          `${process.env.REACT_APP_S3URL}/img_${imgName[i]}.${imgFormat[i]}`,
        );
      }
      setSelectedImg(sImage);
      setInputData({
        ...inputData,
        imageTitle: uploadRes[0],
        imageFormat: uploadRes[1],
      });
      setLoader(false);
    } catch (e) {
      alert.error("File upload failed");
      setLoader(false);
    }
  };

  const onPDFChange = async (e) => {
    try {
      setLoader(true);
      const formData = new FormData();
      formData.append("pdf", e.target.files[0], e.target.files[0].name);
      const uploadRes = await api.uploadPDF(formData);
      setSelectedPDF(`${process.env.REACT_APP_S3URL}/${uploadRes}`);
      setInputData({ ...inputData, pdfFile: `${uploadRes}` });
      setLoader(false);
    } catch (e) {
      alert.error("File upload failed");
      setLoader(false);
    }
  };

  const removeImage = (index) => {
    const newImages = [...selectedImg];
    newImages.splice(index, 1);
    setSelectedImg(newImages);
    const imgArray = inputData.imageTitle.split(",");
    const fmtArray = inputData.imageFormat.split(",");
    imgArray.splice(index, 1);
    fmtArray.splice(index, 1);
    setInputData({
      ...inputData,
      imageTitle: imgArray.join(","),
      imageFormat: fmtArray.join(","),
    });
  };

  const removePDF = () => {
    setSelectedPDF(null);
    setInputData({ ...inputData, pdfFile: "" });
  };

  return (
    <>
      {/* ── Description ── */}
      <div className="edit-form-section">
        <label className="edit-section-label">
          <i className="fas fa-align-left me-2"></i>Description
        </label>
        <ReactQuill
          placeholder="Add description"
          modules={Editor}
          theme="snow"
          value={inputData.description}
          onChange={(e) => setInputData({ ...inputData, description: e })}
          className="edit-quill"
        />
      </div>

      {/* ── Trade Type Section ── */}
      <div className="edit-form-section">
        <label className="edit-section-label">
          <i className="fas fa-chart-line me-2"></i>Trade Details (Optional)
        </label>

        {/* Toggle */}
        <div className="trade-type-toggle">
          <button
            type="button"
            className={`trade-type-btn ${tradeType === "spot" ? "active-spot" : ""}`}
            onClick={() => handleTradeTypeChange("spot")}
          >
            <i className="fas fa-coins me-2"></i>Spot Trade
          </button>
          <button
            type="button"
            className={`trade-type-btn ${tradeType === "futures" ? "active-futures" : ""}`}
            onClick={() => handleTradeTypeChange("futures")}
          >
            <i className="fas fa-bolt me-2"></i>Futures Trade
          </button>
          {tradeType && (
            <button
              type="button"
              className="trade-type-btn trade-type-clear"
              onClick={() => setTradeType(null)}
            >
              <i className="fas fa-times me-2"></i>Clear
            </button>
          )}
        </div>

        {/* ── Spot Fields ── */}
        {tradeType === "spot" && (
          <div className="trade-fields-container trade-fields-spot">
            <div className="trade-fields-badge">
              <i className="fas fa-coins"></i> Spot Trade
            </div>
            <div className="trade-grid">
              <div className="trade-field">
                <label className="trade-label">Exchange</label>
                <select
                  name="exchange"
                  className="trade-input"
                  value={spotTrade.exchange}
                  onChange={handleSpotChange}
                >
                  <option value="">Not specified</option>
                  <option value="MEXC">MEXC</option>
                  <option value="OKX">OKX</option>
                  <option value="Bitget">Bitget</option>
                  <option value="KuCoin">KuCoin</option>
                </select>
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Coin <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="coin"
                  className="trade-input"
                  placeholder="e.g. BTC"
                  value={spotTrade.coin}
                  onChange={handleSpotChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Entry Price – Buy Limit{" "}
                  <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="entryPrice"
                  className="trade-input"
                  placeholder="e.g. 67500"
                  value={spotTrade.entryPrice}
                  onChange={handleSpotChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Take Profit – Sell Limit{" "}
                  <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="takeProfit"
                  className="trade-input"
                  placeholder="e.g. 72000"
                  value={spotTrade.takeProfit}
                  onChange={handleSpotChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">Stop Loss (SL)</label>
                <input
                  type="number"
                  name="stopLoss"
                  className="trade-input"
                  placeholder="e.g. 63000 (optional)"
                  value={spotTrade.stopLoss}
                  onChange={handleSpotChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Filled Amount (USDT) <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="filledAmountUSDT"
                  className="trade-input"
                  placeholder="e.g. 1000"
                  value={spotTrade.filledAmountUSDT}
                  onChange={handleSpotChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Filled Quantity (COIN){" "}
                  <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="filledQuantityCoin"
                  className="trade-input"
                  placeholder="e.g. 0.01481"
                  value={spotTrade.filledQuantityCoin}
                  onChange={handleSpotChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Status <span className="required-star">*</span>
                </label>
                <select
                  name="status"
                  className="trade-input trade-select"
                  value={spotTrade.status}
                  onChange={handleSpotChange}
                  disabled={["Closed", "TP Hit", "SL Hit"].includes(spotTrade.status)}
                >
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                  {/* TP Hit and SL Hit are set automatically by cron — not selectable by admin */}
                  {/* <option value="TP Hit">TP Hit</option> */}
                  {/* <option value="SL Hit">SL Hit</option> */}
                  {/* Show current value if already set by system */}
                  {["TP Hit", "SL Hit"].includes(spotTrade.status) && (
                    <option value={spotTrade.status}>{spotTrade.status}</option>
                  )}
                </select>
              </div>
              {spotTrade.status !== "Open" && (
                <div className="trade-field">
                  <label className="trade-label">
                    Closed Price <span className="required-star">*</span>
                  </label>
                  <input
                    type="number"
                    name="closedPrice"
                    className="trade-input"
                    placeholder="e.g. 71500"
                    value={spotTrade.closedPrice}
                    onChange={handleSpotChange}
                  />
                </div>
              )}
            </div>
            <p className="trade-required-note">
              <span className="required-star">*</span> Required fields
            </p>
          </div>
        )}

        {/* ── Futures Fields ── */}
        {tradeType === "futures" && (
          <div className="trade-fields-container trade-fields-futures">
            <div className="trade-fields-badge futures-badge">
              <i className="fas fa-bolt"></i> Futures Trade
            </div>
            <div className="trade-grid">
              <div className="trade-field">
                <label className="trade-label">Exchange</label>
                <select
                  name="exchange"
                  className="trade-input"
                  value={futuresTrade.exchange}
                  onChange={handleFuturesChange}
                >
                  <option value="">Not specified</option>
                  <option value="MEXC">MEXC</option>
                  <option value="OKX">OKX</option>
                  <option value="Bitget">Bitget</option>
                  <option value="KuCoin">KuCoin</option>
                </select>
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Coin <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="coin"
                  className="trade-input"
                  placeholder="e.g. ETH"
                  value={futuresTrade.coin}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Position Type <span className="required-star">*</span>
                </label>
                <select
                  name="positionType"
                  className="trade-input trade-select"
                  value={futuresTrade.positionType}
                  onChange={handleFuturesChange}
                >
                  <option value="">Select</option>
                  <option value="Long">Long</option>
                  <option value="Short">Short</option>
                </select>
              </div>
              <div className="trade-field">
                <label className="trade-label">Leverage</label>
                <div className="trade-input-suffix">
                  <input
                    type="number"
                    name="leverage"
                    className="trade-input"
                    placeholder="e.g. 10"
                    value={futuresTrade.leverage}
                    onChange={handleFuturesChange}
                  />
                  <span className="suffix-label">x</span>
                </div>
              </div>
              <div className="trade-field">
                <label className="trade-label">Margin Mode</label>
                <select
                  name="marginMode"
                  className="trade-input trade-select"
                  value={futuresTrade.marginMode}
                  onChange={handleFuturesChange}
                >
                  <option value="">Select</option>
                  <option value="Cross">Cross</option>
                  <option value="Isolated">Isolated</option>
                </select>
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Entry Price <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="entryPrice"
                  className="trade-input"
                  placeholder="e.g. 3200"
                  value={futuresTrade.entryPrice}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Stop Loss (SL) <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="stopLoss"
                  className="trade-input"
                  placeholder="e.g. 3050"
                  value={futuresTrade.stopLoss}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Take Profit (TP) <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  name="takeProfit"
                  className="trade-input"
                  placeholder="e.g. 3600"
                  value={futuresTrade.takeProfit}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">Balance Used (USDT) <span className="required-star">*</span></label>
                <input
                  type="number"
                  name="marginUsedUSDT"
                  className="trade-input"
                  placeholder="e.g. 500"
                  value={futuresTrade.marginUsedUSDT}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">Position Size (USDT)</label>
                <input
                  type="number"
                  name="positionSizeUSDT"
                  className="trade-input"
                  placeholder="e.g. 5000"
                  value={futuresTrade.positionSizeUSDT}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">Liquidation Price</label>
                <input
                  type="number"
                  name="liquidationPrice"
                  className="trade-input"
                  placeholder="e.g. 2900"
                  value={futuresTrade.liquidationPrice}
                  onChange={handleFuturesChange}
                />
              </div>
              <div className="trade-field">
                <label className="trade-label">
                  Status <span className="required-star">*</span>
                </label>
                <select
                  name="status"
                  className="trade-input trade-select"
                  value={futuresTrade.status}
                  onChange={handleFuturesChange}
                  disabled={["Closed", "TP Hit", "SL Hit"].includes(futuresTrade.status)}
                >
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                  {/* TP Hit and SL Hit are set automatically by cron — not selectable by admin */}
                  {/* <option value="TP Hit">TP Hit</option> */}
                  {/* <option value="SL Hit">SL Hit</option> */}
                  {["TP Hit", "SL Hit"].includes(futuresTrade.status) && (
                    <option value={futuresTrade.status}>{futuresTrade.status}</option>
                  )}
                </select>
              </div>
              {futuresTrade.status !== "Open" && (
                <div className="trade-field">
                  <label className="trade-label">
                    Closed Price <span className="required-star">*</span>
                  </label>
                  <input
                    type="number"
                    name="closedPrice"
                    className="trade-input"
                    placeholder="e.g. 3550"
                    value={futuresTrade.closedPrice}
                    onChange={handleFuturesChange}
                  />
                </div>
              )}
            </div>
            <p className="trade-required-note">
              <span className="required-star">*</span> Required fields
            </p>
          </div>
        )}
      </div>

      {/* ── Video ── */}
      <div className="edit-form-section">
        <label className="edit-section-label">
          <i className="fas fa-video me-2"></i>Video Embed (Optional)
        </label>
        <textarea
          name="video"
          className="form-control edit-textarea"
          onChange={(e) =>
            setInputData({ ...inputData, video: e.target.value })
          }
          value={inputData?.video}
          placeholder='Paste iframe code here (e.g., <iframe src="...">)'
          rows="3"
        />
      </div>

      {/* ── Image Upload ── */}
      <div className="edit-form-section">
        <label className="edit-section-label">
          <i className="fas fa-images me-2"></i>Images (Optional)
        </label>
        <div className="edit-file-upload-row">
          <label className="edit-upload-btn">
            <input
              name="file"
              onChange={onFileChange}
              accept="image/*"
              type="file"
              multiple
            />
            <i className="fas fa-cloud-upload-alt me-2"></i>Upload Images
          </label>
        </div>

        {/* Preview: newly uploaded */}
        {selectedImg.length > 0 && (
          <div className="edit-image-preview-grid">
            {selectedImg.map((val, key) => (
              <div className="edit-image-preview-card" key={key}>
                <img src={val} alt={`Preview ${key + 1}`} />
                <button
                  className="edit-remove-img-btn"
                  onClick={() => removeImage(key)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Preview: existing from DB */}
        {selectedImg.length === 0 && inputData.imageTitle && (
          <div className="edit-image-preview-grid">
            {inputData.imageTitle.split(",").map((val, key) => (
              <div className="edit-image-preview-card" key={key}>
                <img
                  src={`${process.env.REACT_APP_S3URL}/img_${val}.${inputData.imageFormat.split(",")[key]}`}
                  alt=""
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── PDF Upload ── */}
      <div className="edit-form-section">
        <label className="edit-section-label">
          <i className="fas fa-file-upload me-2"></i>Document (Optional)
        </label>
        {!selectedPDF && !inputData.pdfFile ? (
          <label className="edit-upload-btn">
            <input
              name="file"
              onChange={onPDFChange}
              accept=".pdf,.doc,.docx"
              type="file"
            />
            <i className="fas fa-file-upload me-2"></i>Upload Document
          </label>
        ) : (
          <div className="edit-pdf-preview">
            <i
              className={`fas ${inputData?.pdfFile?.includes(".pdf") ? "fa-file-pdf pdf-icon" : "fa-file-word word-icon"} me-2`}
            ></i>
            <span>{inputData?.pdfFile?.split("_")[0] || "Uploaded File"}</span>
            <div className="edit-pdf-actions">
              <a
                href={
                  selectedPDF ||
                  `${process.env.REACT_APP_S3URL}/${inputData.pdfFile}`
                }
                download={inputData?.pdfFile}
                target="_blank"
                rel="noopener noreferrer"
                className="edit-pdf-action-btn"
              >
                <i className="fas fa-download"></i>
              </a>
              <button
                className="edit-pdf-action-btn edit-pdf-remove"
                onClick={removePDF}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Submit ── */}
      <div className="edit-submit-row">
        <button onClick={handleSubmit} className="btn-submit" disabled={loader}>
          {loader ? (
            <>
              <i className="fas fa-spinner fa-spin me-2"></i>Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>Save Changes
            </>
          )}
        </button>
      </div>

      {loader && (
        <div className="seekoLoader">
          <div className="loader">
            <img src={Logo} alt="loader" />
          </div>
        </div>
      )}
    </>
  );
}
